import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { RootState, useAppSelector } from '../../../../state/state.tsx';
import { selectEffectiveUID, selectActiveRole } from '../../../../state/UserStatesV2/userSliceV2.ts';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Chip, 
    Button, 
    Box, 
    Typography, 
    Card, 
    CardContent,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Snackbar
} from '@mui/material';
import { CheckCircle, Cancel, Schedule, FilterList, PersonAdd } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

interface MarkupClass {
    id: string;
    absentDate: string;
    comments: string;
    consumerId: string;
    consumerMarkupRef: string;
    consumerSubscriptionId: string;
    createdAt: any;
    lastSyncedAt: any;
    providerId: string;
    scheduledDate: string;
    slotName: string;
    status: 'scheduled' | 'approved' | 'cancelled';
    subscriptionId: string;
    updatedAt: any;
    updatedBy: string;
    updaterId: string;
    statusComments?: string;
    statusHistory?: Array<{
        status: string;
        comment: string;
        updatedBy: string;
        updatedAt: any;
    }>;
}

const MarkupClassesPage: React.FC = () => {
    const [markupClasses, setMarkupClasses] = useState<MarkupClass[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedClass, setSelectedClass] = useState<MarkupClass | null>(null);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<'approved' | 'cancelled'>('approved');
    const [statusComment, setStatusComment] = useState<string>('');
    const [updating, setUpdating] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const [hasMore, setHasMore] = useState(false);
    const [approvingAll, setApprovingAll] = useState(false);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>('all');
    const [dateFilter, setDateFilter] = useState<{ start: dayjs.Dayjs | null; end: dayjs.Dayjs | null }>({
        start: dayjs(),
        end: null
    });

    const providerId = useAppSelector(selectEffectiveUID);
    const activeRole = useAppSelector(selectActiveRole);
    const allSubscriptions = useAppSelector((state: RootState) => state.subscriptionTableSlice.data) || []; // eslint-disable-line react-hooks/exhaustive-deps

    const fetchMarkupClasses = useCallback(async () => {
        if (!providerId) return;
        
        try {
            setLoading(true);
            setError(null);
            
            const markupRef = collection(db, `ServiceProviders/${providerId}/markup`);
            
            // Build query with simple date filtering
            let q;
            
            // Add date filter if dateFilter is set - use only single date to avoid index requirements
            if (dateFilter.start) {
                // Filter by single date only
                const filterDate = dateFilter.start.format('YYYY-MM-DD');
                q = query(
                    markupRef, 
                    where('scheduledDate', '==', filterDate),
                    orderBy('createdAt', 'desc')
                );
            } else {
                // No date filter, just order by createdAt
                q = query(markupRef, orderBy('createdAt', 'desc'));
            }
            const snapshot = await getDocs(q);
            
            const classes = snapshot.docs.map(doc => ({
                id: doc.id,
                ...(doc.data() as Omit<MarkupClass, 'id'>)
            })) as MarkupClass[];
            
            setMarkupClasses(classes);
        } catch (err) {
            console.error('Error fetching markup classes:', err);
            setError('Failed to fetch markup classes');
        } finally {
            setLoading(false);
        }
    }, [providerId, dateFilter]);

    useEffect(() => {
        fetchMarkupClasses();
    }, [fetchMarkupClasses]);

    const handleMarkAttendance = () => {
        setToastMessage('Mark Attendance feature is coming soon!');
        setToastOpen(true);
    };

    const handleApproveAll = async () => {
        if (!providerId || scheduledClasses.length === 0) return;

        try {
            setApprovingAll(true);
            setError('');
            
            const currentTime = new Date();
            const currentUser = 'Coach';
            
            // Create status history entry
            const statusHistoryEntry = {
                status: 'approved',
                comment: 'Bulk approved by coach',
                updatedBy: currentUser,
                updatedAt: currentTime
            };

            // Update all scheduled classes
            const updatePromises = scheduledClasses.map(async (markupClass) => {
                const existingHistory = markupClass.statusHistory || [];
                const updatedHistory = [...existingHistory, statusHistoryEntry];
                
                const markupDocRef = doc(db, `ServiceProviders/${providerId}/markup/${markupClass.id}`);
                await updateDoc(markupDocRef, {
                    status: 'approved',
                    statusComments: 'Bulk approved by coach',
                    statusHistory: updatedHistory,
                    updatedAt: currentTime,
                    updatedBy: currentUser,
                    updaterId: providerId
                });
            });

            await Promise.all(updatePromises);
            
            // Refresh the data
            await fetchMarkupClasses();
            
            setToastMessage(`Successfully approved ${scheduledClasses.length} classes!`);
            setToastOpen(true);
        } catch (err) {
            console.error('Error approving all classes:', err);
            setError(`Failed to approve all classes: ${err.message || 'Unknown error'}`);
        } finally {
            setApprovingAll(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!selectedClass || !providerId) return;

        try {
            setUpdating(true);
            setError('');
            
            const currentTime = new Date();
            const currentUser = 'Coach'; // You might want to get this from user state
            
            console.log('Updating status for class:', selectedClass.id);
            console.log('Consumer markup ref:', selectedClass.consumerMarkupRef, typeof selectedClass.consumerMarkupRef);
            
            // Create status history entry
            const statusHistoryEntry = {
                status: newStatus,
                comment: statusComment || '',
                updatedBy: currentUser,
                updatedAt: currentTime
            };
            
            // Get existing status history or create new array
            const existingHistory = selectedClass.statusHistory || [];
            const updatedHistory = [...existingHistory, statusHistoryEntry];
            
            // Update the markup class status
            const markupDocRef = doc(db, `ServiceProviders/${providerId}/markup/${selectedClass.id}`);
            console.log('Updating markup doc:', markupDocRef.path);
            
            await updateDoc(markupDocRef, {
                status: newStatus,
                statusComments: statusComment || '',
                statusHistory: updatedHistory,
                updatedAt: currentTime,
                updatedBy: currentUser,
                updaterId: providerId
            });
            
            console.log('Successfully updated markup class status');

            // Update the consumer markup reference
            if (selectedClass.consumerMarkupRef) {
                try {
                    // Handle both string path and Firestore reference
                    let consumerMarkupRef;
                    if (typeof selectedClass.consumerMarkupRef === 'string') {
                        consumerMarkupRef = doc(db, selectedClass.consumerMarkupRef);
                    } else {
                        // If it's already a Firestore reference, use it directly
                        consumerMarkupRef = selectedClass.consumerMarkupRef;
                    }
                    
                    await updateDoc(consumerMarkupRef, {
                        status: newStatus,
                        statusComments: statusComment || '',
                        statusHistory: updatedHistory,
                        updatedAt: currentTime,
                        updatedBy: currentUser,
                        updaterId: providerId
                    });
                } catch (refError) {
                    console.warn('Could not update consumer markup reference:', refError);
                    // Continue execution even if consumer reference update fails
                }
            }

            // Refresh the data
            await fetchMarkupClasses();
            
            console.log('Status update completed successfully');
            setStatusDialogOpen(false);
            setSelectedClass(null);
            setStatusComment('');
        } catch (err) {
            console.error('Error updating status:', err);
            console.error('Error details:', {
                message: err.message,
                code: err.code,
                stack: err.stack
            });
            setError(`Failed to update status: ${err.message || 'Unknown error'}`);
        } finally {
            setUpdating(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved':
                return 'success';
            case 'cancelled':
                return 'error';
            case 'scheduled':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'approved':
                return <CheckCircle />;
            case 'cancelled':
                return <Cancel />;
            case 'scheduled':
                return <Schedule />;
            default:
                return <Schedule />;
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSubscriptionName = (subscriptionId: string) => {
        const subscription = allSubscriptions.find(sub => sub.id === subscriptionId);
        return subscription ? subscription.Name : 'Unknown Subscription';
    };


    // Get available subscriptions based on role
    const availableSubscriptions = useMemo(() => {
        // Owner and Admin can see all subscriptions
        if (!activeRole || activeRole.role === 'Administrator') {
            return allSubscriptions;
        }
        
        // For other roles, only show assigned subscriptions
        if (activeRole.assignedSubscriptions && activeRole.assignedSubscriptions.length > 0) {
            return allSubscriptions.filter(sub => 
                activeRole.assignedSubscriptions.some(assignedSub => 
                    typeof assignedSub === 'string' ? assignedSub === sub.id : assignedSub.id === sub.id
                )
            );
        }
        
        return [];
    }, [allSubscriptions, activeRole]); // eslint-disable-line react-hooks/exhaustive-deps


    // Filter markup classes based on all criteria
    const filteredClasses = useMemo(() => {
        return markupClasses.filter(cls => {
            // Status filter
            const statusMatch = filterStatus === 'all' || cls.status === filterStatus;
            
            // Subscription filter - when "all" is selected, show all classes that user has access to
            // When specific subscription is selected, filter by that subscription
            const subscriptionMatch = selectedSubscriptionId === 'all' || cls.subscriptionId === selectedSubscriptionId;
            
            // Date filtering is now handled at the query level for better performance
            
            return statusMatch && subscriptionMatch;
        });
    }, [markupClasses, filterStatus, selectedSubscriptionId]);

    // Paginated classes
    const paginatedClasses = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginated = filteredClasses.slice(0, endIndex);
        
        setHasMore(endIndex < filteredClasses.length);
        return paginated;
    }, [filteredClasses, currentPage, itemsPerPage]);

    // Get scheduled classes for approve all
    const scheduledClasses = useMemo(() => {
        return filteredClasses.filter(markupClass => markupClass.status === 'scheduled');
    }, [filteredClasses]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Manage Markup Classes
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Filters Section */}
                <Card sx={{ 
                    mb: 3, 
                    background: 'linear-gradient(135deg, #7a8b9a 0%, #8a9ba8 100%)',
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '20px'
                }}>
                    <CardContent sx={{ py: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <FilterList sx={{ color: 'white' }} />
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                Filter Markup Classes
                            </Typography>
                        </Box>
                        
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                            {/* Date Filter - Single Date Picker */}
                            <Box sx={{ minWidth: 200 }}>
                                <DatePicker
                                    label="Scheduled Date"
                                    value={dateFilter.start}
                                    onChange={(newValue) => setDateFilter({ start: newValue, end: null })}
                                    slotProps={{ 
                                        textField: { 
                                            size: 'small',
                                            sx: { 
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'rgba(255,255,255,0.3)',
                                                        borderRadius: '20px',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'rgba(255,255,255,0.5)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'white',
                                                        borderWidth: 2,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: '#667eea',
                                                    fontWeight: 500,
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#2c3e50',
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: 'white',
                                                    fontWeight: 500,
                                                }
                                            }
                                        } 
                                    }}
                                />
                            </Box>

                            {/* Subscription Filter */}
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel sx={{ 
                                    color: '#667eea', 
                                    fontWeight: 500,
                                    '&.Mui-focused': {
                                        color: '#2c3e50',
                                    }
                                }}>Subscription</InputLabel>
                                <Select
                                    value={selectedSubscriptionId}
                                    label="Subscription"
                                    onChange={(e) => setSelectedSubscriptionId(e.target.value)}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: 'white',
                                                '& .MuiMenuItem-root': {
                                                    color: '#2c3e50',
                                                    fontWeight: 500,
                                                    '&:hover': {
                                                        backgroundColor: '#f8f9fa',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#e3f2fd',
                                                        color: '#1976d2',
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'rgba(255,255,255,0.3)',
                                                borderRadius: '20px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(255,255,255,0.5)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'white',
                                                borderWidth: 2,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#667eea',
                                            fontWeight: 500,
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#2c3e50',
                                        },
                                        '& .MuiSelect-select': {
                                            color: 'white',
                                            fontWeight: 500,
                                        },
                                        '& .MuiSelect-icon': {
                                            color: 'white',
                                        }
                                    }}
                                >
                                    <MenuItem value="all">All Subscriptions</MenuItem>
                                    {availableSubscriptions.map((subscription) => (
                                        <MenuItem key={subscription.id} value={subscription.id}>
                                            {subscription.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Status Filter */}
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel sx={{ 
                                    color: '#667eea', 
                                    fontWeight: 500,
                                    '&.Mui-focused': {
                                        color: '#2c3e50',
                                    }
                                }}>Status</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Status"
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: 'white',
                                                '& .MuiMenuItem-root': {
                                                    color: '#2c3e50',
                                                    fontWeight: 500,
                                                    '&:hover': {
                                                        backgroundColor: '#f8f9fa',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#e3f2fd',
                                                        color: '#1976d2',
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'rgba(255,255,255,0.3)',
                                                borderRadius: '20px',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(255,255,255,0.5)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'white',
                                                borderWidth: 2,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#667eea',
                                            fontWeight: 500,
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#2c3e50',
                                        },
                                        '& .MuiSelect-select': {
                                            color: 'white',
                                            fontWeight: 500,
                                        },
                                        '& .MuiSelect-icon': {
                                            color: 'white',
                                        }
                                    }}
                                >
                                    <MenuItem value="scheduled">📅 Scheduled</MenuItem>
                                    <MenuItem value="approved">✅ Approved</MenuItem>
                                    <MenuItem value="cancelled">❌ Cancelled</MenuItem>
                                    <MenuItem value="all">🔍 All</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Quick Date Buttons */}
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant={dateFilter.start?.isSame(dayjs(), 'day') ? 'contained' : 'outlined'}
                                    onClick={() => setDateFilter({ start: dayjs(), end: null })}
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2,
                                        backgroundColor: dateFilter.start?.isSame(dayjs(), 'day') ? 'white' : 'transparent',
                                        color: dateFilter.start?.isSame(dayjs(), 'day') ? '#7a8b9a' : 'white',
                                        borderColor: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            borderColor: 'white',
                                        }
                                    }}
                                >
                                    📅 Today
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setDateFilter({ start: dayjs().add(1, 'day'), end: null })}
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2,
                                        color: 'white',
                                        borderColor: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            borderColor: 'white',
                                        }
                                    }}
                                >
                                    📅 Tomorrow
                                </Button>
                            </Stack>

                            {/* Approve All Button */}
                            {scheduledClasses.length > 0 && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<CheckCircle />}
                                    onClick={handleApproveAll}
                                    disabled={approvingAll}
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2,
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#45a049',
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'rgba(255,255,255,0.3)',
                                            color: 'rgba(255,255,255,0.7)',
                                        }
                                    }}
                                >
                                    {approvingAll ? 'Approving...' : `Approve All (${scheduledClasses.length})`}
                                </Button>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{
                    borderRadius: '20px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.08)'
                }}>
                    <Box sx={{
                        background: 'linear-gradient(135deg, #8a9ba8 0%, #7a8b9a 100%)',
                        color: 'white',
                        p: 2
                    }}>
                        <Typography variant="h6" sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            📚 Markup Classes ({paginatedClasses.length} of {filteredClasses.length})
                        </Typography>
                    </Box>

                        {filteredClasses.length === 0 ? (
                            <Box textAlign="center" py={6} sx={{ bgcolor: 'grey.50' }}>
                                <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                                    📅 No markup classes found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    No markup classes scheduled for the selected date
                                </Typography>
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Absent Date</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Scheduled Date</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Subscription</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Slot</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Comments</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Status</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Requested By</TableCell>
                                            <TableCell sx={{ 
                                                fontWeight: 500, 
                                                color: '#6b7280',
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedClasses.map((markupClass) => (
                                            <TableRow key={markupClass.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {markupClass.absentDate}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {markupClass.scheduledDate}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                                        {getSubscriptionName(markupClass.subscriptionId)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap>
                                                        {markupClass.slotName}
                                                    </Typography>
                                                </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                maxWidth: 200, 
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                lineHeight: 1.4,
                                                minHeight: '2.8em'
                                            }}
                                            title={markupClass.comments}
                                        >
                                            {markupClass.comments}
                                        </Typography>
                                    </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(markupClass.status)}
                                                        label={markupClass.status}
                                                        color={getStatusColor(markupClass.status) as any}
                                                        size="small"
                                                        sx={{ textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {markupClass.updatedBy}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {markupClass.status === 'scheduled' && (
                                                        <Box display="flex" gap={0.5}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<CheckCircle />}
                                                                onClick={() => {
                                                                    setSelectedClass(markupClass);
                                                                    setNewStatus('approved');
                                                                    setStatusComment('');
                                                                    setStatusDialogOpen(true);
                                                                }}
                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="error"
                                                                startIcon={<Cancel />}
                                                                onClick={() => {
                                                                    setSelectedClass(markupClass);
                                                                    setNewStatus('cancelled');
                                                                    setStatusComment('');
                                                                    setStatusDialogOpen(true);
                                                                }}
                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Box>
                                                    )}
                                                    {markupClass.status === 'approved' && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<PersonAdd />}
                                                            onClick={handleMarkAttendance}
                                                            sx={{ 
                                                                minWidth: 'auto', 
                                                                px: 1.5,
                                                                py: 0.5,
                                                                fontSize: '0.75rem',
                                                                borderRadius: '16px',
                                                                borderColor: '#667eea',
                                                                color: '#667eea',
                                                                '&:hover': {
                                                                    borderColor: '#5a6fd8',
                                                                    color: '#5a6fd8',
                                                                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                                                }
                                                            }}
                                                        >
                                                            Attendance
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Load More Button */}
                        {hasMore && (
                            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1,
                                        borderColor: '#7a8b9a',
                                        color: '#7a8b9a',
                                        '&:hover': {
                                            borderColor: '#5a6c7d',
                                            color: '#5a6c7d',
                                            backgroundColor: 'rgba(122, 139, 160, 0.08)',
                                        }
                                    }}
                                >
                                    Load More ({filteredClasses.length - paginatedClasses.length} remaining)
                                </Button>
                            </Box>
                        )}
                </Card>

            {/* Status Update Dialog */}
            <Dialog 
                open={statusDialogOpen} 
                onClose={() => setStatusDialogOpen(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                    color: 'white',
                    p: 3,
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <DialogTitle sx={{ 
                        color: 'white', 
                        fontWeight: 700, 
                        fontSize: '1.5rem',
                        p: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        🎯 Update Markup Class Status
                    </DialogTitle>
                </Box>
                
                <DialogContent sx={{ p: 3 }}>
                    {selectedClass && (
                        <Box>
                            {/* Class Details */}
                            <Card sx={{ 
                                mb: 3, 
                                p: 3, 
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                border: '1px solid rgba(0,0,0,0.12)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                            }}>
                                <Typography variant="h6" gutterBottom sx={{ 
                                    fontWeight: 600, 
                                    color: '#2c3e50',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    📋 Class Details
                                </Typography>
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Box sx={{ 
                                            flex: 1, 
                                            minWidth: '200px',
                                            p: 2, 
                                            bgcolor: 'white', 
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Student ID
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                {selectedClass.consumerId}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            flex: 1, 
                                            minWidth: '200px',
                                            p: 2, 
                                            bgcolor: 'white', 
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Absent Date
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                {selectedClass.absentDate}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Box sx={{ 
                                            flex: 1, 
                                            minWidth: '200px',
                                            p: 2, 
                                            bgcolor: 'white', 
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Scheduled Date
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                {selectedClass.scheduledDate}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            flex: 1, 
                                            minWidth: '200px',
                                            p: 2, 
                                            bgcolor: 'white', 
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Slot Name
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {selectedClass.slotName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: 'white', 
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            Original Comments
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {selectedClass.comments}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Card>

                            {/* Status History */}
                            {selectedClass.statusHistory && selectedClass.statusHistory.length > 0 && (
                                <Card sx={{ 
                                    mb: 3, 
                                    p: 3, 
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #fef9e7 0%, #f4f1de 100%)',
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                }}>
                                    <Typography variant="h6" gutterBottom sx={{ 
                                        fontWeight: 600, 
                                        color: '#2c3e50',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        📈 Status History
                                    </Typography>
                                    <Stack spacing={2}>
                                        {selectedClass.statusHistory.map((entry, index) => (
                                            <Box key={index} sx={{ 
                                                p: 2, 
                                                bgcolor: 'white', 
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                borderLeft: '4px solid',
                                                borderLeftColor: entry.status === 'approved' ? '#4caf50' : 
                                                               entry.status === 'cancelled' ? '#f44336' : '#ff9800'
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                                        {entry.status === 'approved' ? '✅' : entry.status === 'cancelled' ? '❌' : '📅'} {entry.status}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(entry.updatedAt)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    by {entry.updatedBy}
                                                </Typography>
                                                {entry.comment && (
                                                    <Typography variant="body2" sx={{ 
                                                        fontStyle: 'italic',
                                                        p: 1,
                                                        bgcolor: 'grey.50',
                                                        borderRadius: '4px'
                                                    }}>
                                                        "{entry.comment}"
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Card>
                            )}
                            
                            {/* Status Update Form */}
                            <Card sx={{ 
                                p: 3, 
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)',
                                border: '1px solid rgba(0,0,0,0.12)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                            }}>
                                <Typography variant="h6" gutterBottom sx={{ 
                                    fontWeight: 600, 
                                    color: '#2c3e50',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 3
                                }}>
                                    ✏️ Update Status
                                </Typography>
                                
                                <Stack spacing={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>New Status</InputLabel>
                                        <Select
                                            value={newStatus}
                                            label="New Status"
                                            onChange={(e) => setNewStatus(e.target.value as 'approved' | 'cancelled')}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                }
                                            }}
                                        >
                                            <MenuItem value="approved">✅ Approved</MenuItem>
                                            <MenuItem value="cancelled">❌ Cancelled</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Status Comment"
                                        placeholder="Add a comment about this status change..."
                                        value={statusComment}
                                        onChange={(e) => setStatusComment(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </Stack>
                            </Card>
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ 
                    p: 3, 
                    bgcolor: '#f8f9fa',
                    gap: 2,
                    borderTop: '1px solid rgba(0,0,0,0.08)'
                }}>
                    <Button 
                        onClick={() => {
                            setStatusDialogOpen(false);
                            setStatusComment('');
                        }} 
                        disabled={updating}
                        variant="outlined"
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleStatusUpdate} 
                        variant="contained" 
                        disabled={updating}
                        startIcon={updating ? <CircularProgress size={20} /> : null}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1a252f 0%, #2c3e50 100%)',
                            }
                        }}
                    >
                        {updating ? 'Updating...' : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast Notification */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={4000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setToastOpen(false)} 
                    severity="info" 
                    sx={{ 
                        width: '100%',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Box>
        </LocalizationProvider>
    );
};

export default MarkupClassesPage;
